import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TemplateService } from '../../services/template.service';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ProjecttemplateService } from '../../services/projecttemplate.service';
import { AssignProjectTemplatesRequest, ProjectTemplateModel } from '../../models/project.model';
import { TEMPLATE_TYPE } from '../../models/template.constants';

@Component({
  selector: 'app-assign-template',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MultiSelectModule,
    ButtonModule,
    TooltipModule
  ],
  templateUrl: './assign-template.html',
  styleUrls: ['./assign-template.css']
})
export class AssignTemplate implements OnInit, OnDestroy {
  projectId!: number;
  availableTemplates: any[] = [];
  assignedTemplates: ProjectTemplateModel[] = [];
  selectedTemplateIds: number[] = [];
  dataLoaded = false;
  isSaving = false;
  private destroy$ = new Subject<void>();
  private allTemplates: any[] = []; // Cache all templates to avoid reloading

  constructor(
    private templateService: TemplateService,
    private projecttemplateService: ProjecttemplateService,
    private messageService: MessageService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.projectId = this.config.data?.projectId;

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required'
      });
      this.ref.close();
      return;
    }

    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTemplateDisplayName(template: any): string {
    const shortCode = (
      template?.shortCode ||
      template?.templateShortCode ||
      template?.shortcode ||
      template?.ShortCode ||
      template?.short_code ||
      ''
    ).toString().trim();

    return shortCode || (template?.templateName || '').toString().trim();
  }


  private loadData(): void {
    this.dataLoaded = false;

    // Load both all templates AND assigned templates
    forkJoin({
      allTemplates: this.templateService.getTemplateList(TEMPLATE_TYPE.TEMPLATE),
      assignedTemplates: this.projecttemplateService.getProjectTemplatesByType(this.projectId, TEMPLATE_TYPE.TEMPLATE)
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        // Cache all templates for future use
        this.allTemplates = (result.allTemplates || []).filter(t => t.isActive);
        this.assignedTemplates = result.assignedTemplates || [];

        this.filterAvailableTemplates();
        this.dataLoaded = true;
        this.cdr.detectChanges();

        console.log('Available templates:', this.availableTemplates);
        console.log('Assigned templates:', this.assignedTemplates);
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load templates'
        });
        this.dataLoaded = true;
      }
    });
  }

  private filterAvailableTemplates(): void {
    const assignedMasterIds = new Set(
      this.assignedTemplates.map(t => t.masterTemplateId ?? t.templateId)
    );

    // Filter to show only unassigned templates in dropdown
    this.availableTemplates = this.allTemplates
      .filter(t => !assignedMasterIds.has(t.templateId))
      .map(t => ({
        ...t,
        displayLabel: `${this.getTemplateDisplayName(t)} - ${t.typeName || 'N/A'}`
      }));
  }

  private refreshAssignedTemplates(): void {
    // Only reload assigned templates, not all templates
    this.projecttemplateService.getProjectTemplatesByType(this.projectId, TEMPLATE_TYPE.TEMPLATE)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.assignedTemplates = templates || [];
          this.filterAvailableTemplates();
          this.cdr.detectChanges();
          console.log('Refreshed - Available:', this.availableTemplates.length, 'Assigned:', this.assignedTemplates.length);
        },
        error: (error) => {
          console.error('Error refreshing assigned templates:', error);
        }
      });
  }

  moveTemplateUp(index: number): void {
    if (index === 0) return;
    [this.assignedTemplates[index - 1], this.assignedTemplates[index]] = [this.assignedTemplates[index], this.assignedTemplates[index - 1]];
    this.assignedTemplates = [...this.assignedTemplates];
    this.saveSortOrder();
  }

  moveTemplateDown(index: number): void {
    if (index === this.assignedTemplates.length - 1) return;
    [this.assignedTemplates[index], this.assignedTemplates[index + 1]] = [this.assignedTemplates[index + 1], this.assignedTemplates[index]];
    this.assignedTemplates = [...this.assignedTemplates];
    this.saveSortOrder();
  }

  private saveSortOrder(): void {
    const items = this.assignedTemplates.map((t, i) => ({
      templateId: t.templateId!,
      sortOrder: i + 1
    }));
    this.projecttemplateService.batchUpdateProjectTemplateSortOrder(items)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => console.error('Error saving sort order:', error)
      });
  }

  removeTemplate(templateId: number): void {
    if (!templateId) return;

    this.isSaving = true;
    this.projecttemplateService.unassignProjectTemplate(this.projectId, templateId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSaving = false;
          this.cdr.detectChanges(); // Force UI update
        })
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Template unassigned successfully'
          });
          // Optimized refresh - only reload assigned templates
          this.refreshAssignedTemplates();
        },
        error: (error) => {
          console.error('Error unassigning template:', error);

          // Check if this is a 409 Conflict (template has associated data)
          if (error.status === 409 && error.error?.hasAssociatedData) {
            const errorMsg = error.error.message || 'Data is assigned to this template. Please remove the data before unassigning.';
            this.messageService.add({
              severity: 'warn',
              summary: 'Cannot Unassign Template',
              detail: errorMsg,
              life: 6000 // Show for 6 seconds
            });
          } else {
            // Generic error
            const errorMsg = error.error?.message || 'Failed to unassign template';
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMsg
            });
          }
          // Ensure UI updates after showing error
          this.cdr.detectChanges();
        }
      });
  }

  save(): void {
    if (this.selectedTemplateIds.length === 0) {
      return;
    }

    const assignedIds = new Set(this.assignedTemplates.map(t => t.masterTemplateId ?? t.templateId));
    const uniqueSelected = Array.from(new Set(this.selectedTemplateIds));
    const filteredSelected = uniqueSelected.filter(id => !assignedIds.has(id));

    if (filteredSelected.length !== uniqueSelected.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Duplicate Removed',
        detail: 'Some templates were already assigned and were removed from selection.'
      });
      this.selectedTemplateIds = filteredSelected;
      this.cdr.detectChanges();
    }

    if (filteredSelected.length === 0) {
      return;
    }

    this.isSaving = true;
    const request: AssignProjectTemplatesRequest = {
      projectId: this.projectId,
      templateIds: filteredSelected
    };

    this.projecttemplateService.assignProjectTemplates(request).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isSaving = false)
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Templates assigned successfully'
        });
        // Clear selection and optimized refresh
        this.selectedTemplateIds = [];
        this.refreshAssignedTemplates();
      },
      error: (error) => {
        console.error('Error assigning templates:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to assign templates'
        });
      }
    });
  }

  cancel(): void {
    this.ref.close();
  }
}
